import {
    Flex,
    Box,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionIcon,
    AccordionPanel,
    Image as ChakraImage,
    IconButton,
    useBreakpointValue,
    Link as ChakraLink,
} from "@chakra-ui/react";
import { StyleSheet, css } from "aphrodite";
import { useSelector } from "react-redux";
import React, { useEffect, useMemo, useState } from "react";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

const isValidHttpUrl = (value) => {
  if (!value) return false;
  try {
    const u = new URL(String(value));
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const ImageSlider = ({
  images = [],
  links = [],               // optional links
  interval = 4000,
  showDots = true,
  showArrows = true,
  bg = "gray.50",
  borderRadius = "12px",
  is_french = false,
}) => {
  const [current, setCurrent] = useState(0);

  const maxW = useBreakpointValue({ base: "100%", md: "50%" });
  const ratio = useBreakpointValue({ base: 4 / 3, md: 16 / 9 });

  const safeImages = useMemo(() => images.filter(Boolean), [images]);

  useEffect(() => {
    if (safeImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((p) => (p + 1) % safeImages.length);
    }, interval);
    return () => clearInterval(timer);
  }, [safeImages.length, interval]);

  useEffect(() => {
    if (current >= safeImages.length) setCurrent(0);
  }, [safeImages.length, current]);

  if (!safeImages.length) return null;

  const prevSlide = () =>
    setCurrent((p) => (p - 1 + safeImages.length) % safeImages.length);

  const nextSlide = () =>
    setCurrent((p) => (p + 1) % safeImages.length);

  const currentLink = links?.[current];
  const hasLink = isValidHttpUrl(currentLink);

  return (
    <Box w="100%" maxW={maxW} mx="auto" mt={4}>
      {/* Image Area */}
      <Box
        position="relative"
        w="100%"
        sx={{ aspectRatio: `${ratio}` }}
        bg={bg}
        borderRadius={borderRadius}
        overflow="hidden"
      >
        <ChakraImage
          src={safeImages[current]}
          alt={`slide-${current}`}
          w="100%"
          h="100%"
          objectFit="contain"
          p={{ base: 2, md: 4 }}
          userSelect="none"
          draggable={false}
        />

        {showArrows && safeImages.length > 1 && (
          <>
            <IconButton
              aria-label="Previous image"
              icon={<ChevronLeftIcon boxSize={7} />}
              onClick={prevSlide}
              position="absolute"
              top="50%"
              left={{ base: "8px", md: "12px" }}
              transform="translateY(-50%)"
              borderRadius="full"
              bg="whiteAlpha.800"
              _hover={{ bg: "whiteAlpha.900" }}
            />

            <IconButton
              aria-label="Next image"
              icon={<ChevronRightIcon boxSize={7} />}
              onClick={nextSlide}
              position="absolute"
              top="50%"
              right={{ base: "8px", md: "12px" }}
              transform="translateY(-50%)"
              borderRadius="full"
              bg="whiteAlpha.800"
              _hover={{ bg: "whiteAlpha.900" }}
            />
          </>
        )}
      </Box>

      {/* Optional Link Text */}
      {hasLink && (
        <Flex justify="center" mt={2}>
          <ChakraLink
            href={currentLink}
            isExternal
            fontSize="m"
            fontWeight="medium"
            color="blue.500"
            _hover={{ textDecoration: "underline" }}
          > 
          {is_french
          ?<u>Cliquez pour en savoir plus</u>
          :<u>Click for Learn more</u>
          }
          </ChakraLink>
        </Flex>
      )}

      {/* Dots */}
      {showDots && safeImages.length > 1 && (
        <Flex justify="center" mt={3} gap={2}>
          {safeImages.map((_, i) => (
            <Box
              key={i}
              w="10px"
              h="10px"
              borderRadius="full"
              bg={i === current ? "blue.500" : "gray.300"}
              cursor="pointer"
              onClick={() => setCurrent(i)}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default ImageSlider;